import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { setAccessToken, clearAccessToken } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Try to restore session on mount (via refresh token cookie)
  useEffect(() => {
    const initAuth = async () => {
      try {
        const res = await authService.refresh();
        const { accessToken, user: userData } = res.data.data;
        setAccessToken(accessToken);
        setUser(userData);
      } catch {
        // No valid refresh token — stay logged out
        clearAccessToken();
        setUser(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };
    initAuth();
  }, []);

  const login = useCallback(async (credentials) => {
    const res = await authService.login(credentials);
    const data = res.data.data || {};
    const token = data.accessToken || data.token;
    const userData = data.user;
    if (token) setAccessToken(token);
    if (userData) setUser(userData);
    return userData;
  }, []);

  const signup = useCallback(async (data) => {
    const res = await authService.signup(data);
    const payload = res.data.data || {};
    // New OTP flow: register no longer auto-authenticates. Caller routes to /verify-otp.
    if (payload.requires_verification) {
      return {
        requiresVerification: true,
        email: payload.email,
        devOtp: payload.dev_otp || null,
      };
    }
    // Backwards-compatible fallback (in case backend ever returns a token)
    const token = payload.accessToken || payload.token;
    if (token) setAccessToken(token);
    if (payload.user) setUser(payload.user);
    return { user: payload.user, organization: payload.organization };
  }, []);

  const verifyOtp = useCallback(async ({ email, otp }) => {
    const res = await authService.verifyOtp({ email, otp });
    const data = res.data.data || {};
    const token = data.accessToken || data.token;
    const userData = data.user;
    if (token) setAccessToken(token);
    if (userData) setUser(userData);
    return userData;
  }, []);

  const resendOtp = useCallback(async (email) => {
    const res = await authService.resendOtp({ email });
    return res.data?.data?.dev_otp || null;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore errors on logout
    } finally {
      clearAccessToken();
      setUser(null);
    }
  }, []);

  const updateUser = useCallback((updatedFields) => {
    setUser((prev) => (prev ? { ...prev, ...updatedFields } : null));
  }, []);

  // Check role
  const hasRole = useCallback(
    (...roles) => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user]
  );

  // Check admin
  const isAdmin = useCallback(() => {
    return hasRole('super_admin', 'owner', 'project_manager');
  }, [hasRole]);

  const value = {
    user,
    loading,
    initialized,
    isAuthenticated: !!user,
    login,
    signup,
    verifyOtp,
    resendOtp,
    logout,
    updateUser,
    hasRole,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
